import React, { useEffect, useState, useMemo } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import WarningIcon from '@mui/icons-material/Warning'

// components
import StyledTable from '../../components/ReactTable/StyledTable'
import StyledTableContainer from '../../components/StyledTableContainer/StyledTableContainer'
import StyledEmptyHeader from '../../components/StyledEmptyHeader/StyledEmptyHeader'
import StyledSelectField from '../../components/StyledSelectField/StyledSelectField'

// services
import ToolLifeTrackingService from '../../service/toolLifeTracking.service'

const ToolLifeTracking = () => {
  const defaultToolIdNo = {
    value: 'all',
    label: 'All tool id'
  }
  const defaultToolType = {
    value: 'all',
    label: 'All tool type'
  }
  const defaultToolSize = {
    value: 'all',
    label: 'All tool size'
  }

  const [tools, setTools] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const [toolIdOptions, setToolIdOptions] = useState([])
  const [toolTypeOptions, setToolTypeOptions] = useState([])
  const [toolSizeOptions, setToolSizeOptions] = useState([])

  const [toolId, setToolId] = useState(defaultToolIdNo)
  const [toolType, setToolType] = useState(defaultToolType)
  const [toolSize, setToolSize] = useState(defaultToolSize)

  const fetchData = async () => {
    setIsLoading(true)
    try {
      const usageRes = await ToolLifeTrackingService.getToolsUsage()
      const usageData = usageRes.data.data
      const optionsToolId = {}
      const optionsToolType = {}
      const optionsToolSize = {}

      // populate filter options
      usageData.forEach((tool) => {
        optionsToolId[tool.toolId] = {
          value: tool.toolId,
          label: tool.toolId
        }
        optionsToolType[tool.toolType] = {
          value: tool.toolType,
          label: tool.toolType
        }
        optionsToolSize[tool.toolSize] = {
          value: tool.toolSize,
          label: tool.toolSize
        }
      })

      setToolIdOptions(Object.values(optionsToolId))
      setToolTypeOptions(Object.values(optionsToolType))
      setToolSizeOptions(Object.values(optionsToolSize))
      setTools(usageData)
    } catch (err) {
      console.error('Tool fetch error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const filters = {
    ...(toolId.value !== 'all' && { toolId: toolId.value }),
    ...(toolType.value !== 'all' && { toolType: toolType.value }),
    ...(toolSize.value !== 'all' && { toolSize: toolSize.value })
  }

  const filteredRows = useMemo(() => {
    return tools.filter((row) =>
      Object.entries(filters).every(([key, val]) => row[key] === val)
    )
  }, [tools, toolId, toolType, toolSize])

  const getToolStatusProps = (toolLife, usedLife) => {
    const remainingLife = toolLife - usedLife
    const lifePercent = toolLife > 0 ? (remainingLife / toolLife) * 100 : 100

    if (remainingLife <= 0) {
      return {
        label: 'Expired',
        color: 'default',
        icon: <WarningIcon style={{ color: 'red' }} />
      }
    }

    if (lifePercent <= 10) {
      return {
        label: 'Low life',
        color: 'default',
        icon: <WarningIcon style={{ color: 'yellow' }} />
      }
    }

    return {
      label: 'Healthy',
      color: 'success',
      icon: null
    }
  }

  const columns = [
    { Header: 'S.No', accessor: 'index', Cell: ({ row }) => row.index + 1 },
    { Header: 'Tool ID', accessor: 'toolId' },
    { Header: 'Tool type', accessor: 'toolType' },
    { Header: 'Tool size', accessor: 'toolSize' },
    { Header: 'Tool life', accessor: 'toolLife' },
    { Header: 'Used life', accessor: 'usedLife' },
    {
      Header: 'Remaining life',
      accessor: 'remainingLife',
      Cell: ({ row }) => {
        const { toolLife, usedLife } = row.original
        return toolLife - usedLife
      }
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row }) => {
        const { toolLife, usedLife } = row.original
        const statusProps = getToolStatusProps(toolLife, usedLife)

        return (
          <Chip
            label={statusProps.label}
            color={statusProps.color}
            size="small"
            icon={statusProps.icon}
          />
        )
      }
    }
  ]

  return (
    <>
      <Stack
        sx={{
          position: 'sticky',
          top: 0,
          width: '100%',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '12px',
          backgroundColor: '#fff',
          padding: '16px',
          borderBottom: '1px solid #eee',
          zIndex: 10
        }}
      >
        <Stack
          spacing={2}
          direction="row"
          sx={{ width: '100%', alignItems: 'center' }}
        >
          <StyledSelectField
            placeHolderText="Tool ID"
            selectedValue={toolId}
            selectOptions={[defaultToolIdNo, ...toolIdOptions]}
            onChangeHandler={setToolId}
            isMulti={false}
            isSearchable={true}
            noOptionsMessage="No tool ID"
          />
          <StyledSelectField
            placeHolderText="Tool type"
            selectedValue={toolType}
            selectOptions={[defaultToolType, ...toolTypeOptions]}
            onChangeHandler={setToolType}
            isMulti={false}
            isSearchable={true}
            noOptionsMessage="No type"
          />
          <StyledSelectField
            placeHolderText="Tool size"
            selectedValue={toolSize}
            selectOptions={[defaultToolSize, ...toolSizeOptions]}
            onChangeHandler={setToolSize}
            isMulti={false}
            isSearchable={true}
            noOptionsMessage="No size"
          />
        </Stack>
      </Stack>

      <Box sx={{ padding: '16px' }}>
        <StyledTableContainer>
          {isLoading && <StyledEmptyHeader>Loading...</StyledEmptyHeader>}
          {!isLoading && filteredRows.length === 0 && (
            <StyledEmptyHeader>
              There are no tool life records
            </StyledEmptyHeader>
          )}
          {!isLoading && filteredRows.length > 0 && (
            <StyledTable
              pagination={false}
              columns={columns}
              data={filteredRows}
              hiddenColumns={[]}
            />
          )}
        </StyledTableContainer>
      </Box>
    </>
  )
}

export default ToolLifeTracking
